import React from "react";
import { Box, Paper, Typography, Chip, LinearProgress } from "@mui/material";
import { styled } from "@mui/material/styles";

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(0.5),
  backgroundColor: "white",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

const SegmentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(0.5),
  padding: theme.spacing(1),
  borderRadius: "6px",
  backgroundColor: "rgba(0,0,0,0.02)",
  border: "1px solid rgba(0,0,0,0.05)",
  "&:hover": {
    backgroundColor: "rgba(63, 81, 181, 0.05)",
    transform: "scale(1.01)",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
  },
}));

const ColorIndicator = styled(Box)(({ color }) => ({
  width: "16px",
  height: "16px",
  borderRadius: "50%",
  backgroundColor: color,
  marginRight: "12px",
  border: "2px solid white",
  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
}));

const LabelBox = styled(Box)(({ theme }) => ({
  flex: 1,
  marginRight: theme.spacing(2),
}));

const ValueBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  minWidth: "80px",
}));

const PercentageBar = styled(LinearProgress)(({ theme, color }) => ({
  width: "60px",
  height: "6px",
  borderRadius: "3px",
  backgroundColor: "rgba(0,0,0,0.1)",
  marginTop: "4px",
  "& .MuiLinearProgress-bar": {
    borderRadius: "3px",
    backgroundColor: color,
  },
}));

const MaterialPieChart = ({
  data,
  title = "Distribución",
  colors = [
    "#3f51b5",
    "#f50057",
    "#4caf50",
    "#ff9800",
    "#9c27b0",
    "#00bcd4",
    "#795548",
    "#607d8b",
  ],
}) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <ChartContainer>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography>No hay datos disponibles</Typography>
      </ChartContainer>
    );
  }

  // Procesar datos
  const processedData = data.map((item, index) => {
    if (typeof item === "object" && item.name && item.value !== undefined) {
      return item;
    }
    if (Array.isArray(item) && item.length >= 2) {
      return { name: item[0], value: item[1] };
    }
    return { name: `Segmento ${index + 1}`, value: item };
  });

  const total = processedData.reduce((sum, item) => sum + item.value, 0);

  // Ordenar por valor descendente
  const sortedData = [...processedData].sort((a, b) => b.value - a.value);

  return (
    <ChartContainer>
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: "bold", fontSize: "1rem" }}
        >
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Chip
            label={`${sortedData.length} categorías`}
            size="small"
            variant="outlined"
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {sortedData.slice(0, 4).map((item, index) => {
          const percentage = total > 0 ? (item.value / total) * 100 : 0;
          const color = colors[index % colors.length];

          return (
            <SegmentContainer key={index}>
              <ColorIndicator color={color} />

              <LabelBox>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mb: 0.2,
                    fontSize: "0.8rem",
                  }}
                  title={item.name}
                >
                  {item.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                >
                  {/* Mostrar count si está disponible, si no mostrar value */}
                  {item.count !== undefined
                    ? `${item.count} elementos`
                    : `${item.value} elementos`}
                </Typography>
              </LabelBox>

              <ValueBox>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: color }}
                >
                  {/* Si el valor ya es un porcentaje (string), mostrarlo; si no, calcularlo */}
                  {typeof item.value === "string" && item.value.includes(".")
                    ? `${item.value}%`
                    : `${percentage.toFixed(2)}%`}
                </Typography>
                <PercentageBar
                  variant="determinate"
                  value={
                    typeof item.value === "string"
                      ? parseFloat(item.value)
                      : percentage
                  }
                  color={color}
                />
              </ValueBox>
            </SegmentContainer>
          );
        })}
        {sortedData.length > 4 && (
          <Box
            sx={{
              textAlign: "center",
              mt: 1,
              color: "text.secondary",
              fontSize: "0.75rem",
            }}
          >
            +{sortedData.length - 4} categorías más
          </Box>
        )}
      </Box>
    </ChartContainer>
  );
};

export default MaterialPieChart;
